import React, { useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useGetAgendaWithIndividualAssignmentQuery } from "../../api/useGetAgendaWithIndividualAssignment";
import { Alert, Typography, Container } from "@mui/material";
import { differenceInCalendarWeeks, startOfWeek } from "date-fns";
import AssignmentDialog from "./components/AssignmentDialog";
import { useGetStudentStatusQuery } from "../../api/useGetStudentStatus";
import StatusHeader from "./components/StatusHeader";
import AssignmentTable from "./components/AssignmentTable";
import CriteriaDialog from "./components/CriteriaDialog";

const AssignmentPage = () => {
  const { studyId } = useParams();
  const [openDialog, setOpenDialog] = useState(false);
  const [currentAssignment, setCurrentAssignment] = useState({});
  const [openCriteriaDialog, setOpenCriteriaDialog] = useState(false);
  const [selectedNote, setSelectedNote] = useState("");

  const { data, isLoading, isError, error } =
    useGetAgendaWithIndividualAssignmentQuery(studyId);
  const {
    data: status,
    isLoading: isStatusLoading,
    error: statusError,
  } = useGetStudentStatusQuery(studyId);

  const handleOpenDialog = (agenda) => {
    setCurrentAssignment(agenda);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleRowInteraction = (note) => {
    if (note) {
      setSelectedNote(note);
      setOpenCriteriaDialog(true);
    }
  };

  const handleCloseCriteriaDialog = () => {
    setOpenCriteriaDialog(false);
    setSelectedNote("");
  };

  const groupedByWeek = useMemo(() => {
    if (!data || data.length === 0) return {};

    const startOfFirstWeek = startOfWeek(new Date(data[0].closeDate), {
      weekStartsOn: 1,
    });

    return data.reduce((acc, agenda) => {
      const weekNumber = differenceInCalendarWeeks(
        new Date(agenda.closeDate),
        startOfFirstWeek,
        { weekStartsOn: 1 }
      );
      if (!acc[weekNumber]) acc[weekNumber] = [];
      acc[weekNumber].push(agenda);
      return acc;
    }, {});
  }, [data]);

  if (isLoading) return <div>Loading...</div>;
  if (isError)
    return <Alert severity="error">{error.response.data.message}</Alert>;

  return (
    <Container
      sx={{
        pb: 8,
        px: { xs: 1, sm: 2, md: 3 },
      }}
    >
      <StatusHeader status={status} />

      {Object.entries(groupedByWeek).map(([week, agendas], index) => (
        <div key={index}>
          <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
            {parseInt(week) + 1}주차
          </Typography>
          <AssignmentTable
            agendas={agendas}
            onRowInteraction={handleRowInteraction}
            onSubmit={handleOpenDialog}
            userStatus={status}
          />
        </div>
      ))}

      <AssignmentDialog
        open={openDialog}
        onClose={handleCloseDialog}
        currentAssignment={currentAssignment}
      />

      <CriteriaDialog
        open={openCriteriaDialog}
        onClose={handleCloseCriteriaDialog}
        note={selectedNote}
      />
    </Container>
  );
};

export default AssignmentPage;
